
import { DashboardData, defaultDashboardData } from '@/types/dashboard';
import { fetchAllDashboardData } from './dashboardFetch';
import { calculateTrend } from './metricCalculations';
import { processNewsData, extractNewsItems } from './newsDataProcessor';
import { processLeaksData, countRecentLeaks } from './leakDataProcessor';
import { processCVEData } from './vulnerabilityProcessor';
import { processRansomwareData } from './ransomwareProcessor';
import { processEOLData, countUpcomingEOLItems } from './eolDataProcessors';
import { findMostAttackedCountry } from './countryDataProcessors';
import { getNewsFileName } from './newsFileSelector';

/**
 * Fetch and process all dashboard data
 */
export async function fetchDashboardData(language: string = 'en', days: number = 30): Promise<DashboardData> {
  try {
    const [_, leaksData, ransomwareData, cveData, __, [newsEnData, newsPlData], eolData, releasesData] =
      await fetchAllDashboardData();

    // Merge news data
    const mergedNews = {
      items: [
        ...(newsEnData?.items || newsEnData?.news || Array.isArray(newsEnData) ? newsEnData : []).map((item: any) => ({ ...item, lang: 'en' })),
        ...(newsPlData?.items || newsPlData?.news || Array.isArray(newsPlData) ? newsPlData : []).map((item: any) => ({ ...item, lang: 'pl' }))
      ]
    };

    const recentLeaks = processLeaksData(leaksData);
    const leaksCounts = countRecentLeaks(leaksData, days);
    const { recentRansomwareAttacks, allRansomwareAttacks, mostActiveGroup, victimCount, previousVictimCount } = processRansomwareData(ransomwareData, days);
    const {
      criticalVulnerabilities,
      highVulnerabilities,
      mediumVulnerabilities,
      lowVulnerabilities,
      recentCriticalVulnerabilities,
      recentHighVulnerabilities,
      recentMediumVulnerabilities,
      recentLowVulnerabilities,
      prevCriticalCount,
      prevHighCount,
      prevMediumCount,
      prevLowCount
    } = processCVEData(cveData, days);


    const latestNews = processNewsData(mergedNews);
    const allNewsItems = extractNewsItems(mergedNews);
    const upcomingEOL = processEOLData(eolData || {});
    const eolItemsCounts = countUpcomingEOLItems(eolData || {}, days);
    const mostAttackedCountry = findMostAttackedCountry(ransomwareData || []);

    const normalizedReleases = Array.isArray(releasesData)
      ? releasesData
      : (releasesData?.releases && Array.isArray(releasesData.releases) ? releasesData.releases : []);

    const allReleasesData = normalizedReleases
      .sort((a: any, b: any) => new Date(b.data_wydania).getTime() - new Date(a.data_wydania).getTime());



    const latestReleases = allReleasesData; // Return all, let UI slice

    return {
      eolItemsCount: calculateTrend('eolItemsCount', eolItemsCounts.current, eolItemsCounts.previous),
      leaksCount: calculateTrend('leaksCount', leaksCounts.current, leaksCounts.previous),
      mostActiveRansomwareGroup: mostActiveGroup,
      mostAttackedCountry,
      ransomwareVictimsCount: calculateTrend('ransomwareVictimsCount', victimCount, previousVictimCount),
      criticalVulnerabilitiesCount: calculateTrend('criticalVulnerabilities', criticalVulnerabilities.length, prevCriticalCount),
      highVulnerabilitiesCount: calculateTrend('highVulnerabilities', highVulnerabilities.length, prevHighCount),
      mediumVulnerabilitiesCount: calculateTrend('mediumVulnerabilities', mediumVulnerabilities.length, prevMediumCount),
      lowVulnerabilitiesCount: calculateTrend('lowVulnerabilities', lowVulnerabilities.length, prevLowCount),
      latestNews,
      allNews: allNewsItems,
      recentLeaks,
      recentCriticalVulnerabilities, // Already processed in processCVEData
      recentHighVulnerabilities,
      recentMediumVulnerabilities,
      recentLowVulnerabilities,
      upcomingEOL,
      latestReleases,
      allReleases: allReleasesData,
      recentRansomwareAttacks, // Return all, let UI slice
      allRansomwareAttacks
    };
  } catch (error) {
    console.error('Error in fetchDashboardData:', error);
    return defaultDashboardData;
  }
}
